import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getValidationRules } from '../../helpers/helper';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';

function stateFromProps(member = {}) {
  const socialMedia = member.socialMedia || {};
  return {
    facebook: socialMedia.facebook || '',
    instagram: socialMedia.instagram || '',
    twitter: socialMedia.twitter || '',
    linkedIn: socialMedia.linkedIn || '',
    website: socialMedia.website || '',
    youtube: socialMedia.youtube || '',
    awareness: socialMedia.awareness || '',
  };
}

/**
 * Form to update the current logged user
 */
function SocialMediaForm({ t, onMemberChange, member, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'socialMediaForm';
  const validationRules = useMemo(
    () =>
      getValidationRules(
        requiredFieldsPerBlock,
        PROFILE_BLOCK.SOCIAL_MEDIA,
        {},
        { url: true },
      ),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const handleSubmit = async () => {
    const params = {
      socialMedia: {
        facebook: state.facebook,
        instagram: state.instagram,
        twitter: state.twitter,
        linkedIn: state.linkedIn,
        website: state.website,
        youtube: state.youtube,
        awareness: state.awareness,
      },
    };
    onMemberChange(params);
  };

  const handleChange = useCallback(
    (field, value) => setState(prevState => ({ ...prevState, [field]: value })),
    [],
  );

  const fixURL = useCallback(
    field => () => {
      setState(prevState => {
        const { [field]: url } = prevState;
        if (
          url &&
          url.substr(0, 7) !== 'http://' &&
          url.substr(0, 8) !== 'https://'
        ) {
          return { ...prevState, [field]: `https://${url}` };
        }
        return prevState;
      });
    },
    [],
  );

  const inputProps = useCallback(field => {
    switch (field) {
      case 'facebook':
        return { onBlur: fixURL('facebook') };
      case 'instagram':
        return { onBlur: fixURL('instagram') };
      case 'twitter':
        return { onBlur: fixURL('twitter') };
      case 'linkedIn':
        return { onBlur: fixURL('linkedIn') };
      case 'website':
        return { onBlur: fixURL('website') };
      case 'youtube':
        return { onBlur: fixURL('youtube') };
      case 'awareness':
        return { onBlur: fixURL('awareness') };
      default:
        return undefined;
    }
  }, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_socialMedia')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="facebook"
              value={state.facebook}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="instagram"
              value={state.instagram}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="twitter"
              value={state.twitter}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="linkedIn"
              value={state.linkedIn}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="website"
              value={state.website}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="youtube"
              value={state.youtube}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
            <ProfileField
              field="awareness"
              value={state.awareness}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOCIAL_MEDIA}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
            />
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

SocialMediaForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

SocialMediaForm.defaultProps = {
  onSkipStep: null,
};

export default translate(SocialMediaForm);
